"use client";

import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";

/**
 * On text selection inside children, show Tweet + Copy chips (no third-party scripts).
 */
export function BlogSelectionShare({ articleUrl, articleTitle, children }: { articleUrl: string; articleTitle: string; children: ReactNode }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [text, setText] = useState("");

  const hide = useCallback(() => {
    setPos(null);
    setText("");
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const containsNode = (n: Node | null) => {
      let cur: Node | null = n;
      while (cur) {
        if (cur === root) return true;
        cur = cur.parentNode;
      }
      return false;
    };

    const onSelection = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !containsNode(sel.anchorNode)) {
        hide();
        return;
      }
      const str = sel.toString().trim();
      if (str.length < 12 || str.length > 280) {
        hide();
        return;
      }
      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      setText(str);
      setPos({
        top: rect.top + window.scrollY - 44,
        left: Math.min(window.innerWidth - 160, Math.max(8, rect.left + window.scrollX)),
      });
    };

    const onScroll = () => hide();

    document.addEventListener("selectionchange", onSelection);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("selectionchange", onSelection);
      window.removeEventListener("scroll", onScroll);
    };
  }, [hide]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
    hide();
  };

  const tweet = () => {
    const via = `&via=expirydesk`;
    const quoted = `"${articleTitle.slice(0, 80)}" — ${text.slice(0, 180)}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(quoted)}&url=${encodeURIComponent(articleUrl)}${via}`;
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=400");
    hide();
  };

  return (
    <div ref={rootRef} className="ed-blog-selection-root">
      {children}
      {pos ? (
        <div className="ed-blog-selection-toolbar" style={{ top: pos.top, left: pos.left }} role="toolbar" aria-label="Share selection">
          <button type="button" className="ed-blog-selection-btn" onClick={tweet}>
            Post
          </button>
          <button type="button" className="ed-blog-selection-btn" onClick={copy}>
            Copy
          </button>
        </div>
      ) : null}
    </div>
  );
}
